import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Plus } from "lucide-react";
import { Input } from "@/shared/components/Input";
import { Button } from "@/shared/components/Button";
import { BotaoVoltar } from "@/shared/components/BotaoVoltar";
import { useCategorias, useCriarOuBuscarCategoria } from "@/modules/biblioteca/hooks/useCategorias";
import { useMaiorNumeroCadastro } from "@/modules/biblioteca/hooks/useLivros";
import type { LivroComCategoria } from "@/modules/biblioteca/services/livrosService";
import { proximoNumeroCadastro } from "@/modules/biblioteca/utils/numeroCadastro";
import type { LivroStatus } from "@/shared/types/database.types";

// Limites do formulário. ISBN espelha a coluna do banco (livros.isbn varchar(45)). Título e autor
// são menores que as colunas (livros.nome_livro e livros.autor, varchar(255)) de propósito: 255
// vinha do tamanho da coluna, não de uma regra de negócio. Na produção (02/11/2026, 413 livros) o
// maior título tem 87 caracteres e o maior autor, 55, então nenhum livro existente é bloqueado.
const LIMITE_TITULO = 150;
const LIMITE_ISBN = 45;
const LIMITE_AUTOR = 100;
// livros.numero_cadastro é integer; 6 dígitos cobrem qualquer acervo de igreja.
const LIMITE_NUMERO_CADASTRO = 6;

const formularioSchema = z.object({
  // Número da etiqueta do livro físico. Opcional; em branco = livro sem número. Texto no
  // formulário, convertido para número em GerenciarLivros.
  numeroCadastro: z
    .string()
    .regex(/^\d{0,6}$/, `Use só números, até ${LIMITE_NUMERO_CADASTRO} dígitos.`)
    .refine((v) => v === "" || Number(v) > 0, "O número deve ser maior que zero.")
    .optional(),
  nomeLivro: z
    .string()
    .min(2, "Informe o título do livro.")
    .max(LIMITE_TITULO, `O título pode ter no máximo ${LIMITE_TITULO} caracteres.`),
  isbn: z.string().max(LIMITE_ISBN, `ISBN pode ter no máximo ${LIMITE_ISBN} caracteres.`).optional(),
  autor: z
    .string()
    .min(1, "Informe o autor do livro.")
    .max(LIMITE_AUTOR, `Autor pode ter no máximo ${LIMITE_AUTOR} caracteres.`),
  // UX-06: um livro pode ter mais de uma categoria agora (antes só uma).
  categoriaIds: z.array(z.string()).min(1, "Selecione ao menos uma categoria."),
  quantidadeTotal: z.coerce.number().int().min(0, "Quantidade não pode ser negativa."),
  descricao: z.string().max(255, "A descrição pode ter no máximo 255 caracteres.").optional(),
  status: z.enum(["Disponivel", "Indisponivel", "Excluido"]),
});

export type FormularioLivroValores = z.infer<typeof formularioSchema>;

interface FormularioLivroProps {
  livroEmEdicao?: LivroComCategoria | null;
  carregando: boolean;
  /** Erro vindo do backend (ex: ISBN duplicado), exibido dentro do próprio modal. */
  erro?: string | null;
  /** Avisa o componente pai sempre que o formulário tiver alterações não salvas. */
  onDirtyChange?: (sujo: boolean) => void;
  onSubmit: (dados: FormularioLivroValores) => void;
}

export function FormularioLivro({ livroEmEdicao, carregando, erro, onDirtyChange, onSubmit }: FormularioLivroProps) {
  const ehEdicao = !!livroEmEdicao;
  const { data: categorias } = useCategorias();
  const criarCategoria = useCriarOuBuscarCategoria();

  const [criandoCategoria, setCriandoCategoria] = useState(false);
  const [nomeNovaCategoria, setNomeNovaCategoria] = useState("");
  const [erroCategoria, setErroCategoria] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    getFieldState,
    formState: { errors, isDirty },
  } = useForm<FormularioLivroValores>({
    resolver: zodResolver(formularioSchema),
    defaultValues: { numeroCadastro: "", status: "Disponivel", quantidadeTotal: 1, categoriaIds: [] },
  });

  // Guardado em variável para poder encadear o onChange de limpeza de
  // dígitos sem sobrescrever o onChange do React Hook Form (mesmo motivo
  // do FormularioUsuario — sem isso, "sair sem salvar" nunca detecta
  // mudança nesse campo específico).
  const registroIsbn = register("isbn");
  const registroNumeroCadastro = register("numeroCadastro");

  // Livro novo: o campo já vem com o próximo número livre (o maior já usado + 1), que a pessoa
  // pode trocar. Não conta como alteração do formulário (não dispara "sair sem salvar").
  const { data: maiorNumero } = useMaiorNumeroCadastro(!ehEdicao);

  useEffect(() => {
    onDirtyChange?.(isDirty);
  }, [isDirty, onDirtyChange]);

  const categoriasSelecionadas = watch("categoriaIds") ?? [];
  const descricaoAtual = watch("descricao") ?? "";
  const tituloAtual = watch("nomeLivro") ?? "";
  const isbnAtual = watch("isbn") ?? "";
  const autorAtual = watch("autor") ?? "";
  const numeroCadastroAtual = watch("numeroCadastro") ?? "";

  useEffect(() => {
    if (ehEdicao || maiorNumero === undefined) return;
    if (getFieldState("numeroCadastro").isDirty) return;
    setValue("numeroCadastro", String(proximoNumeroCadastro(maiorNumero)), { shouldDirty: false });
  }, [ehEdicao, maiorNumero, getFieldState, setValue]);

  useEffect(() => {
    if (livroEmEdicao) {
      reset({
        numeroCadastro: livroEmEdicao.numero_cadastro != null ? String(livroEmEdicao.numero_cadastro) : "",
        nomeLivro: livroEmEdicao.nome_livro,
        isbn: livroEmEdicao.isbn ?? "",
        autor: livroEmEdicao.autor ?? "",
        categoriaIds: livroEmEdicao.categorias.map((c) => c.id),
        quantidadeTotal: livroEmEdicao.quantidade_total,
        descricao: livroEmEdicao.descricao ?? "",
        status: livroEmEdicao.status as LivroStatus,
      });
    }
  }, [livroEmEdicao, reset]);

  async function handleCriarCategoria() {
    setErroCategoria(null);
    const nome = nomeNovaCategoria.trim();
    if (!nome) {
      setErroCategoria("Informe um nome para a categoria.");
      return;
    }
    try {
      const novaCategoria = await criarCategoria.mutateAsync(nome);
      setValue("categoriaIds", [...categoriasSelecionadas, novaCategoria.id], {
        shouldValidate: true,
      });
      setCriandoCategoria(false);
      setNomeNovaCategoria("");
    } catch (erroCriacao) {
      setErroCategoria(
        erroCriacao instanceof Error ? erroCriacao.message : "Não foi possível criar a categoria.",
      );
    }
  }

  function contador(valor: string, limite: number) {
    return (
      <p className={`text-right text-xs ${valor.length >= limite ? "font-medium text-warning" : "text-muted"}`}>
        {valor.length}/{limite}
      </p>
    );
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4" noValidate>
      <div className="flex flex-col gap-1">
        <Input
          label="Número de cadastro"
          inputMode="numeric"
          maxLength={LIMITE_NUMERO_CADASTRO}
          erro={errors.numeroCadastro?.message}
          {...registroNumeroCadastro}
          onChange={(e) => {
            e.target.value = e.target.value.replace(/\D/g, "");
            registroNumeroCadastro.onChange(e);
          }}
        />
        {ehEdicao &&
        livroEmEdicao?.numero_cadastro != null &&
        numeroCadastroAtual !== String(livroEmEdicao.numero_cadastro) ? (
          <p className="text-xs text-warning">
            Este número está na etiqueta do livro. Se mudar aqui, mude a etiqueta também.
          </p>
        ) : (
          <p className="text-xs text-muted">
            O mesmo número da etiqueta do livro. Serve para achar o livro na estante. Pode ficar em branco.
          </p>
        )}
      </div>

      <div className="flex flex-col gap-1">
        <Input
          label="Título do livro"
          maxLength={LIMITE_TITULO}
          erro={errors.nomeLivro?.message}
          {...register("nomeLivro")}
        />
        {contador(tituloAtual, LIMITE_TITULO)}
      </div>

      <div className="flex flex-col gap-1">
        <Input
          label="ISBN"
          inputMode="numeric"
          maxLength={LIMITE_ISBN}
          erro={errors.isbn?.message}
          {...registroIsbn}
          onChange={(e) => {
            e.target.value = e.target.value.replace(/\D/g, "");
            registroIsbn.onChange(e);
          }}
        />
        {contador(isbnAtual, LIMITE_ISBN)}
      </div>

      <div className="flex flex-col gap-1">
        <Input
          label="Autor"
          maxLength={LIMITE_AUTOR}
          erro={errors.autor?.message}
          {...register("autor")}
        />
        {contador(autorAtual, LIMITE_AUTOR)}
      </div>

      <div className="flex flex-col gap-1.5">
        <label className="text-sm font-medium text-text">
          Categorias <span className="text-muted">(pode marcar mais de uma)</span>
        </label>

        {!criandoCategoria ? (
          <>
            <div className="flex max-h-40 flex-col gap-1 overflow-y-auto rounded-md border border-border bg-surface-2 p-2">
              {!categorias?.length ? (
                <p className="px-2 py-1 text-xs text-muted">Nenhuma categoria cadastrada ainda.</p>
              ) : (
                categorias.map((cat) => (
                  <label
                    key={cat.id}
                    className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-sm text-text hover:bg-surface"
                  >
                    <input
                      type="checkbox"
                      className="h-4 w-4 accent-brand-verde"
                      checked={categoriasSelecionadas.includes(cat.id)}
                      onChange={() =>
                        setValue(
                          "categoriaIds",
                          categoriasSelecionadas.includes(cat.id)
                            ? categoriasSelecionadas.filter((id) => id !== cat.id)
                            : [...categoriasSelecionadas, cat.id],
                          { shouldValidate: true },
                        )
                      }
                    />
                    {cat.nome}
                  </label>
                ))
              )}
            </div>
            {errors.categoriaIds && (
              <span className="text-xs text-danger">{errors.categoriaIds.message}</span>
            )}
            <button
              type="button"
              onClick={() => setCriandoCategoria(true)}
              className="flex w-fit items-center gap-1 text-xs font-bold text-brand-verde hover:underline"
            >
              <Plus size={13} />
              Criar nova categoria
            </button>
          </>
        ) : (
          <div className="flex flex-col gap-2">
            <div className="flex gap-2">
              <input
                autoFocus
                value={nomeNovaCategoria}
                onChange={(e) => setNomeNovaCategoria(e.target.value)}
                placeholder="Nome da nova categoria"
                className="flex-1 rounded-md border border-transparent bg-surface-2 px-4 py-2.5 text-sm text-text focus:outline-none focus:ring-2 focus:ring-primary"
              />
              <Button
                type="button"
                onClick={handleCriarCategoria}
                carregando={criarCategoria.isPending}
                className="bg-brand-verde text-white"
              >
                Salvar
              </Button>
              <button
                type="button"
                onClick={() => {
                  setCriandoCategoria(false);
                  setErroCategoria(null);
                }}
                className="px-2 text-sm text-muted hover:text-text"
              >
                Cancelar
              </button>
            </div>
            {erroCategoria && <span className="text-xs text-danger">{erroCategoria}</span>}
          </div>
        )}
      </div>

      <Input
        label="Quantidade total de exemplares"
        type="number"
        min={0}
        erro={errors.quantidadeTotal?.message}
        {...register("quantidadeTotal")}
      />
      {ehEdicao && (
        <p className="-mt-2 text-xs text-muted">
          A quantidade disponível é ajustada automaticamente quando o total muda.
        </p>
      )}
      <div className="flex flex-col gap-1.5">
        <label htmlFor="descricao" className="text-sm font-medium text-text">
          Descrição
        </label>
        <input
          id="descricao"
          type="text"
          maxLength={255}
          className={`rounded-md border bg-surface-2 px-4 py-2.5 text-sm text-text placeholder:text-muted focus:outline-none focus:ring-2 focus:ring-primary ${
            errors.descricao ? "border-danger" : "border-transparent"
          }`}
          {...register("descricao")}
        />
        <div className="flex items-center justify-between">
          {errors.descricao ? (
            <span className="text-xs text-danger">{errors.descricao.message}</span>
          ) : (
            <span />
          )}
          <span
            className={`text-xs ${descricaoAtual.length >= 255 ? "font-medium text-warning" : "text-muted"}`}
          >
            {descricaoAtual.length}/255
          </span>
        </div>
      </div>
      <div className="flex flex-col gap-1.5">
        <label className="text-sm font-medium text-text">Status</label>
        <select
          className="rounded-md border border-transparent bg-surface-2 px-4 py-2.5 text-sm text-text focus:outline-none focus:ring-2 focus:ring-primary"
          {...register("status")}
        >
          <option value="Disponivel">Ativo</option>
          <option value="Indisponivel">Inativo</option>
        </select>
        {/* O valor gravado segue "Disponivel"/"Indisponivel" (as funções de empréstimo do banco
            dependem dele); só o nome na tela mudou, para não confundir com "sem exemplar livre". */}
        <p className="text-xs text-muted">
          Ativo: em circulação, pode ser emprestado quando houver exemplar livre. Inativo: fora de
          circulação (perdido, danificado ou guardado); não pode ser emprestado e não aparece para os
          usuários.
        </p>
      </div>

      {/* Erro do backend — exibido DENTRO do modal, não atrás dele. */}
      {erro && (
        <p role="alert" className="rounded-md bg-danger/10 px-3 py-2 text-sm text-danger">
          {erro}
        </p>
      )}

      <Button type="submit" carregando={carregando} className="mt-2">
        {ehEdicao ? "Salvar alterações" : "Criar livro"}
      </Button>
      <BotaoVoltar disabled={carregando} className="-mt-2" />
    </form>
  );
}
